
import { supabase } from "@/lib/supabase";
import { Organization } from "@/types/organizationTypes";
import { formatPhoneNumber } from "@/lib/format";

export const useUpdateOrganizationSettings = (
  organization: Organization | null,
  setOrganization: (org: Organization) => void,
  organizationId?: string
) => {
  return async (settings: Partial<Organization>) => {
    console.log('🔧 useUpdateOrganizationSettings validation:', {
      organizationId: organizationId,
      organizationExists: !!organization,
      organizationName: organization?.name
    });

    if (!organizationId) {
      console.error('❌ useUpdateOrganizationSettings: No organization ID provided');
      throw new Error("No organization ID provided");
    }
    
    if (!organization) {
      console.error('❌ useUpdateOrganizationSettings: Organization data not loaded');
      throw new Error("Organization data not loaded");
    }

    const supabaseCompatibleSettings: Record<string, any> = {};
    if (settings.name !== undefined) supabaseCompatibleSettings.name = settings.name;
    if (settings.contact_email !== undefined) supabaseCompatibleSettings.contact_email = settings.contact_email;
    if (settings.contact_phone !== undefined) {
      // Format phone number before saving
      supabaseCompatibleSettings.contact_phone = formatPhoneNumber(settings.contact_phone);
    }
    if (settings.timezone !== undefined) supabaseCompatibleSettings.timezone = settings.timezone;
    if (settings.logo_url !== undefined) supabaseCompatibleSettings.logo_url = settings.logo_url;
    if (settings.registration_fee_cents !== undefined)
      supabaseCompatibleSettings.registration_fee_cents = settings.registration_fee_cents;
    if (settings.sibling_discount_cents !== undefined)
      supabaseCompatibleSettings.sibling_discount_cents = settings.sibling_discount_cents;
    if (settings.billing_settings) supabaseCompatibleSettings.billing_settings = settings.billing_settings;
    if (settings.class_defaults) supabaseCompatibleSettings.class_defaults = settings.class_defaults;
    if (settings.parent_portal_settings) supabaseCompatibleSettings.parent_portal_settings = settings.parent_portal_settings;
    if (settings.parent_can_self_enroll_trial !== undefined) supabaseCompatibleSettings.parent_can_self_enroll_trial = settings.parent_can_self_enroll_trial;

    console.log('🔧 Updating organization settings:', {
      organizationId,
      settingsToUpdate: supabaseCompatibleSettings,
      originalSettings: settings
    });

    const { data, error } = await supabase
      .from("organizations")
      .update(supabaseCompatibleSettings)
      .eq("id", organizationId)
      .select();

    if (error) {
      console.error('❌ Database update error:', error);
      throw new Error(`Failed to update organization: ${error.message}`);
    }

    console.log('✅ Organization updated successfully:', data);
    setOrganization({ ...organization, ...settings });
  };
};
